// @ts-strict-ignore
import { Box, Spinner } from '@usealloy/component-library';
import debounce from 'lodash/debounce';
import { ErrorBoundary } from 'react-error-boundary';
import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactFlowProps } from 'react-flow-renderer';
import ReactFlow, {
  ReactFlowProvider,
} from 'react-flow-renderer';
import useResizeObserver from 'use-resize-observer';

import EditorErrorFallbackView from 'flow/editor/EditorErrorFallbackView';
import {
  exitLinkedChildActionThunk,
  toggleNodeFolderCollapsibleThunk,
  toggleSnippetCollapsibleThunk,
} from 'flow/store/redux/thunks';
import {
  dismissWorkflowOperationMessage,
  removeAction,
  resetComponentState,
  resetNodeAndConnectorState,
  setFloatedAnchorNodeId,
  setFocusedNodeId,
  setIsTagSwapMode,
  updateCanvasProperties,
} from 'flow/store/redux/slice';
import {
  selectCanvasTranslateExtent,
  selectPreviewAction,
  selectWorkflowGraph,
  selectWorkflowData,
  selectWorkflowProps,
  selectFocusedAction,
  selectWorkflowOperationDescs,
  selectPreviewPrecedenceIndexedAction,
  selectActiveNodeEditorTab,
  selectIsGraphLargerThanCanvas,
  selectIsNodeEditorDrawerOpen,
  selectIsWorkflowGraphEmpty,
  selectSnippetMode,
  selectMergeDiff,
  selectIsPastePendingSnippetMode,
} from 'flow/store/redux/selectors';
import { NODE_EDITOR_DRAWER_BOTTOM_MARGIN, workflowNodeType } from 'flow/service/graph';
import type { WorkflowNode } from 'flow/service/graph';
import { EditorMode } from 'flow/editor/context/constants';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import SnippetCopyPaste from '../SnippetCopyPaste';
import NodeEditorDrawer from '../NodeEditorDrawer';
import { nodeTypes, edgeTypes } from '../Graph';
import { ON_RESIZE_DEBOUNCE_TIME } from './constants';
import { useReactFlowOnMove, useKeyboardKeyEvent } from './hooks';
import Island from '../Island';
import { useEditorAccess } from '../../context';
import { findLastOperation } from 'flow/service/assistance';
import type { WorkflowOperationMessage } from 'flow/store/engine/types';
import { RuleUpdateOperationType } from 'flow/store/engine/update/constants';
import SearchMatchViewportController from './SearchMatchViewportController';
import { getIsEditable } from 'flow/service/general';
import WorkflowCanvasEmptyState from './WorkflowCanvasEmptyState';
import { UserContext } from '~/dashboard/context';
import type { MenuBarModalGroup, MenuBarModalName } from '../MenuBar/MenuBarModals/types';
import { useModalGroup } from 'flow/editor/hooks';
import { getIsInSnippetReviewMode } from 'flow/service/snippets';
import { getIsMergeImpacted } from 'flow/service/changelog/formatters/mergeDropdownFormatter/helpers';
import { StyledSpinnerContainer } from './styles';

interface WorkflowCanvasProps {
  onResize: (entry: ResizeObserverEntry) => void;
  modalGroup: MenuBarModalGroup;
}

function WorkflowCanvas({ onResize, modalGroup }: WorkflowCanvasProps) {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);

  const graph = useAppSelector(selectWorkflowGraph);
  const translateExtent = useAppSelector(selectCanvasTranslateExtent);
  const isGraphLargerThanCanvas = useAppSelector(selectIsGraphLargerThanCanvas);
  const activeNodeEditorTab = useAppSelector(selectActiveNodeEditorTab);

  const workflowData = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);
  // todo BUILD-624 - these will become a single variable once intermediate states are supported
  const previewPrecedenceIndexedAction = useAppSelector(selectPreviewPrecedenceIndexedAction);
  const previewActionBeingBuilt = useAppSelector(selectPreviewAction);
  const focusedAction = useAppSelector(selectFocusedAction);
  const operationDescriptions = useAppSelector(selectWorkflowOperationDescs);
  const isNodeEditorDrawerOpen = useAppSelector(selectIsNodeEditorDrawerOpen);
  const isGraphEmpty = useAppSelector(selectIsWorkflowGraphEmpty);
  const snippetMode = useAppSelector(selectSnippetMode);
  const mergeDiff = useAppSelector(selectMergeDiff);
  const isPastePending = useAppSelector(selectIsPastePendingSnippetMode);

  const [isCanvasFocused, setIsCanvasFocused] = useState<boolean>(false);

  const nodeTypesMemo = useMemo(() => (nodeTypes), []);
  const edgeTypesMemo = useMemo(() => (edgeTypes), []);
  const handleOnResize = React.useMemo(() => debounce(
    (size: { width: number; height: number }) => {
      dispatch(updateCanvasProperties({ rect: size }));
      onResize({ contentRect: size } as ResizeObserverEntry);
    },
    ON_RESIZE_DEBOUNCE_TIME), [dispatch, onResize]);

  const elementRef = useRef<HTMLDivElement | null>(null);
  const {
    ref: reactFlowRef,
  } = useResizeObserver<HTMLDivElement>({
    onResize: handleOnResize,
  });

  // Combine refs to store the element reference
  const combinedRef = React.useCallback((node: HTMLDivElement | null) => {
    elementRef.current = node;
    reactFlowRef(node);
  }, [reactFlowRef]);

  // Trigger resize observer when snippet preview opens/closes
  useEffect(() => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      handleOnResize({ width: rect.width, height: rect.height });
    }
  }, [snippetMode, handleOnResize]);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const dismissLastAssistanceIfExists = React.useCallback(
    ({ messages }: { messages?: WorkflowOperationMessage['message'][] } = {},
    ) => {
      if (focusedAction && operationDescriptions.length) {
        const lastAssistanceMessageToDismiss = findLastOperation({
          operations: operationDescriptions,
          publicId: focusedAction.publicId,
          messages,
        });
        if (lastAssistanceMessageToDismiss) {
          dispatch(dismissWorkflowOperationMessage({ id: lastAssistanceMessageToDismiss.id }));
        }
      }
    }, [dispatch, focusedAction, operationDescriptions]);

  const handleNodeClick: ReactFlowProps['onNodeClick'] = React.useCallback(
    (e: React.MouseEvent, node: WorkflowNode) => {
      const { id: nodeId, type: nodeType } = node;

      if (nodeType === workflowNodeType.exit) {
        return;
      }

      if (nodeType === workflowNodeType.folder) {
        // currently only used for input attributes
        dispatch(toggleNodeFolderCollapsibleThunk());
        return;
      }

      if (nodeType === workflowNodeType.snippet) {
        dispatch(toggleSnippetCollapsibleThunk(nodeId));
        return;
      }

      if (getIsInSnippetReviewMode(snippetMode) && !getIsMergeImpacted(mergeDiff, nodeId)) {
        return;
      }

      if (previewActionBeingBuilt && nodeId !== previewActionBeingBuilt.publicId) {
        dispatch(exitLinkedChildActionThunk(previewActionBeingBuilt.publicId));
      }
      dispatch(setFocusedNodeId(nodeId));
      dismissLastAssistanceIfExists({
        messages: [RuleUpdateOperationType.CANCEL_PENDING_RULE_GROUP],
      });
      /**
       * If the user is adding a new precedence indexed (Island) action and they click on
       * a node (which focuses it), we want to delete said in-flight action as well
       */
      if (previewPrecedenceIndexedAction) {
        dispatch(removeAction({ id: previewPrecedenceIndexedAction.publicId }));
      }
    },
    [
      dispatch,
      previewActionBeingBuilt,
      previewPrecedenceIndexedAction,
      dismissLastAssistanceIfExists,
      snippetMode,
      mergeDiff,
    ]);

  const handleNodeDoubleClick: ReactFlowProps['onNodeDoubleClick'] = React.useCallback(
    (e: React.MouseEvent, node: WorkflowNode) => {
      if (node.type === workflowNodeType.folder) return;

      dispatch(setFloatedAnchorNodeId(node.id));
    }, [dispatch]);

  const handlePaneClick: ReactFlowProps['onPaneClick'] = React.useCallback(() => {
    if (!previewActionBeingBuilt) {
      dispatch(resetComponentState());
      dispatch(resetNodeAndConnectorState());
      dismissLastAssistanceIfExists();

      // If the drawer was open and we're clicking on the canvas, reset tag swap mode
      if (isNodeEditorDrawerOpen) {
        dispatch(setIsTagSwapMode(false));
      }
    }

    /**
     * If the user is adding a new precedence indexed (Island) action and they click on
     * the canvas (which un-focuses the action and closes the island), we want to delete
     * said in-flight action as well
     */
    if (previewPrecedenceIndexedAction) {
      dispatch(removeAction({ id: previewPrecedenceIndexedAction.publicId }));
    }
  }, [
    dispatch,
    dismissLastAssistanceIfExists,
    previewActionBeingBuilt,
    previewPrecedenceIndexedAction,
    isNodeEditorDrawerOpen,
  ]);

  const onEscapeKeyDown = React.useCallback(e => {
    const isActiveTabOutputExpression = activeNodeEditorTab === 'rule';
    if (!isActiveTabOutputExpression) {
      handlePaneClick(e);
    }
  }, [activeNodeEditorTab, handlePaneClick]);

  const handleOnMoveStart: ReactFlowProps['onMoveStart'] = React.useCallback((e, viewport) => {
    dispatch(updateCanvasProperties({ isMoving: true, rect: viewport }));
  }, [dispatch]);

  const handleOnMoveEnd: ReactFlowProps['onMoveEnd'] = React.useCallback((e, viewport) => {
    dispatch(updateCanvasProperties({ isMoving: false, rect: viewport }));
  }, [dispatch]);

  const handleOnMove = useReactFlowOnMove({ start: handleOnMoveStart, end: handleOnMoveEnd });
  // this wrapper is necessary to get around a type error on the Error Boundary
  const onReset = React.useCallback(e => handlePaneClick(e), [handlePaneClick]);

  useKeyboardKeyEvent('Escape', onEscapeKeyDown);

  return (
    <ErrorBoundary
      FallbackComponent={({ ...props }) => (
        <EditorErrorFallbackView
          {...props}
        />
      )}
      onReset={onReset}
    >
      <Box
        direction="column"
        alignItems="stretch"
        style={{ flex: '1 0 auto', cursor: isGraphLargerThanCanvas ? 'grab' : 'default' }}
        onFocus={() => setIsCanvasFocused(true)}
        onBlur={() => setIsCanvasFocused(false)}
        tabIndex={0}
      >
        <SearchMatchViewportController />
        {isPastePending && (
          <StyledSpinnerContainer>
            <Spinner />
          </StyledSpinnerContainer>
        )}
        {isGraphEmpty && featureFlags.ruleLibraryPhase >= 1 ? (
          <WorkflowCanvasEmptyState modalGroup={modalGroup} />
        ) : (
          <ReactFlow
            ref={combinedRef}
            edges={graph.edges}
            edgeTypes={edgeTypesMemo}
            nodes={graph.nodes}
            nodesConnectable={false}
            nodeTypes={nodeTypesMemo}
            onMove={handleOnMove}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={!inEditMode ? undefined : handleNodeDoubleClick}
            onPaneClick={handlePaneClick}
            panOnScroll
            snapToGrid
            translateExtent={translateExtent}
          // guarantees no zooming is allowed
            maxZoom={1}
            minZoom={1}
            zoomActivationKeyCode={null}
            zoomOnDoubleClick={false}
            zoomOnPinch={false}
            zoomOnScroll={false}
          >
            <div onFocus={e => e.stopPropagation()}>
              <Island />
            </div>
          </ReactFlow>
        )}
      </Box>
      {workflowData && workflowProps && inEditMode
        && featureFlags?.copyPasteGA && (
          <SnippetCopyPaste isCanvasFocused={isCanvasFocused} />
      )}
    </ErrorBoundary>
  );
}

const WrappedWorkflowCanvas = ({ readOnly, modalGroup: providedModalGroup }:
{ readOnly?: boolean; modalGroup?: MenuBarModalGroup }) => {
  const workflowData = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const [canvasRect, setCanvasRect] = useState<ResizeObserverEntry | null>(null);

  const createdModalGroup = useModalGroup<MenuBarModalName>();
  const modalGroup = providedModalGroup || createdModalGroup;

  const { setEditorMode } = useEditorAccess();
  useEffect(() => {
    if (readOnly) {
      setEditorMode(EditorMode.ReadOnly);
    }
  }, [readOnly, setEditorMode]);

  const handleOnResize = React.useCallback((entry: ResizeObserverEntry) => {
    setCanvasRect(entry);
  }, []);

  const drawerMaxHeight = useMemo(() => {
    const canvasHeight = canvasRect?.contentRect.height ?? 0;
    const margins = 2 * NODE_EDITOR_DRAWER_BOTTOM_MARGIN; // 2x to leave some space at the top above drawer
    return canvasHeight - margins;
  }, [canvasRect?.contentRect.height]);

  return (
    <ReactFlowProvider>
      <WorkflowCanvas onResize={handleOnResize} modalGroup={modalGroup} />
      {workflowData && workflowProps && (
        <NodeEditorDrawer maxHeight={drawerMaxHeight} />
      )}
    </ReactFlowProvider>
  );
};

export default WrappedWorkflowCanvas;

